import { LuckyModel } from '../../../models/lucky';
import {
  UserModel,
  ImageModel,
  ProductModel,
  CommentModel,
  StreamerModel,
  ConversationType,
  ConversationModel,
  LiveStreamProductModel,
} from '@/models';
import { LiveVideo } from '@/features/livestream/shared';
import { ProductDiscountType } from '@/features/products/shared';
import { AuctionModel } from '@/models/auction';

export { ConversationType };

/**
 * Models
 */

export interface Product extends ProductModel {
  auction?: AuctionModel;
  images: ImageModel[];
  product_discount?: ProductDiscountType;
}
export interface CollectionStickerPayload {
  live_stream_id: number;
}
export type SendAuctionPayload = {
  id: string | number;
  price: string | number;
};

export type giftAtdPayload = {
  liveId: number | string;
  amount: number;
  sessionId: string;
};
export interface ReplyCommentType {
  comment: string | undefined;
  id: number;
  images: string[] | null;
  staffs?: UserModel | undefined;
  updated_at?: string | undefined;
  created_at: string | undefined;
  facebook_comment_info?: CommentFb | null;
}
export type Lottery = {
  created_at: string;
  id: string;
  status: string;
};
export type TicketByUser = {
  id: string;
  number: number;
  users: UserModel;
  created_at: String;
};

interface ThirdPartyUser {
  avatar_url: string | null;
  first_name: string;
  id: number;
  last_name: string | null;
}
interface FbUser extends ThirdPartyUser {
  facebook_id: string;
}
interface YoutubeUser extends ThirdPartyUser {
  youtube_id: string;
}
interface TiktokUser extends ThirdPartyUser {
  tiktok_id: string;
}
interface CommentFb {
  comment_id: number;
  facebook_comment_id: string;
  facebook_user: FbUser;
}
interface CommentYoutube {
  comment_id: number;
  youtube_comment_id: string;
  youtube_user: YoutubeUser;
}
interface CommentTiktok {
  comment_id: number;
  tiktok_comment_id: string;
  tiktok_user: TiktokUser;
}
export interface LiveStreamProduct extends LiveStreamProductModel {
  products: Product;
}

export interface Comment extends CommentModel {
  users: UserModel | null;
  staffs: UserModel;
  reply_comments: ReplyCommentType[];
  facebook_comment_info: CommentFb | null;
  youtube_comment_info: CommentYoutube | null;
  tiktok_comment_info: CommentTiktok | null;
}
export interface MessageData extends CommentModel {
  parent_id?: number;
  comment_id?: number;
  reply_comment_id?: number;
  updated_at?: string | undefined;
  users?: UserModel | undefined;
  staffs?: UserModel | undefined;
  reply_comments?: ReplyCommentType[];
  facebook_comment_info?: CommentFb;
  youtube_comment_info?: CommentYoutube | null;
  tiktok_comment_info?: CommentTiktok | null;
  type?: string;
  code?: string;
  quantity?: number;
  status?: string;
  live_stream_id?: number;
  lucky_items?: LuckyItem[];
  total_in_use?: number;
  last_box_opened_at?: string;
  opened_box_count?: number;
  can_open_next_box?: boolean;
  open_box_continuous_after_sec?: number;
  max_open_per_user?: number;
  lottery_game_id?: string;
  fullname?: string;
  number?: number;
  prize?: { name: string; lottery_prize_id: number; status: string };
  live_stream_product?: PinnedProductTypes;
  product?: {
    id: number;
    code: string;
    image: string;
  };
  ended_at?: string;
  ended_time_at?: string;
  id: number;
  product_id?: number;
  product_code?: string;
  started_at?: string;
  sell_price?: number;
  winner?: {
    fullname: string;
    id: number;
  };
  amount?: number;
}
interface LuckyItem {
  id: number;
  quantity: number;
  in_use: number;
  type: string;
  text: string | null;
  lucky_reward_attachments: { url: string | null }[];
  voucher: string;
}
export interface LuckyDetail {
  id: number;
  code?: string | undefined;
  created_at?: string;
  status?: string;
  last_box_opened_at?: string;
  can_open_next_box?: boolean;
  open_box_continuous_after_sec?: number;
  time_can_open_next_box?: string;
}
export interface MessageDataSocket {
  data: MessageData;
  method: string;
}
export interface LuckyDetailResponse {
  data: LuckyDetail;
}
export type CollectionStickerType = {
  id: number;
  name: string;
  thumbnail: string;
  total: number;
};
export type CollectionStickerResponse = {
  data: CollectionStickerType[];
};
export type StickerType = {
  id: number;
  icon: string;
  name: string;
  collection_id: number;
};
export type LastedMessage = {
  content: string;
  conversation_id: number;
  created_at: string;
  id: number;
  images: string[];
  order_code: string;
  status: boolean;
  type: string;
  user_id: number;
  is_revoked?: boolean;
};
type Conversation = ConversationModel;

/**
 * Query
 */

export type LiveVideosQueryPayload = {
  limit?: number;
  offset?: number;
  live_streamer_id?: string;
  product_keyword?: string;
};
export type StickerPayload = {
  collection?: string;
  offset?: number;
  limit?: number;
};
export type SendStickerPayload = {
  live_stream_id: number;
  sticker_id: number;
  sticker_collection_id: number;
};
export type ListOfParticipantsType = {
  id: string;
  number: number;
  users: UserModel;
  created_at: string;
};
export const LiveStreamStatus = {
  LIVE_NOW: 'LIVE_NOW',
  UNPUBLISHED: 'UNPUBLISHED',
  SCHEDULED_LIVE: 'SCHEDULED_LIVE',
  SCHEDULED_CANCELED: 'SCHEDULED_CANCELED',
  SCHEDULED_UNPUBLISHED: 'SCHEDULED_UNPUBLISHED',
  VOD: 'VOD',
  LIVE: 'LIVE',
};
export type ParticipantsType = {
  number: number;
  fullName: string;
};
export type ProductDepositType = {
  product_id: number;
  fullname: string;
};
export type CreateTicketType = {
  lottery_game_id: string;
};
export type CodeType = {
  code: string;
};
export interface Auction extends AuctionModel {}
export type LiveVideosQueryResponse = { data: LiveVideo[]; total_records: number };
export type StickerQueryResponse = { data: StickerType[] };
export type CreateTicketResponse = { data: CodeType };
export type LiveVideosHomeQueryResponse = { data: LiveVideo[] };
export type LiveVideoQueryResponse = { data: LiveVideo };
export type AuctionResponse = { data: Auction };
export type StreamersQueryPayload = {};
export type ProductDepositPayload = {};
export type ProductDepositResponse = { data: ProductDepositType[] };
export type StreamersQueryResponse = { data: StreamerModel[] };
export type ListOfParticipantsResponse = {
  total_records: number;
  data: ListOfParticipantsType[];
};

export type FormChatPayload = {
  comment: string;
};

export type CommentQueryResponse = {};
export type OpenLuckyQueryResponse = {
  data: LuckyModel;
};

export type CommentQueryPayload = {
  images: null;
  comment: string;
  user_id: number;
  live_stream_id: number;
};
export type NotificationConfigProduct = {
  nhan_nam: boolean;
  nhan_nu: boolean;
  nhan_da_quy: boolean;
  vong_tay: boolean;
  lac_tay: boolean;
  bong_tai: boolean;
  mat_day: boolean;
  ngoc_trai: boolean;
  khac: boolean;
};
export type NotificationConfigPayload = {
  message: boolean;
  order: boolean;
  livestream: boolean;
  all: boolean;
  product: NotificationConfigProduct;
};

export type CommentsQueryResponse = { data: Comment[] };
export type ReplyCommentQueryResponse = { comment: string };
export type TicketByUserResponse = { data: TicketByUser };
export type ProductResponse = { data: Product[] };
export type CommentsQueryPayload = {};

export type CreateChatConversationPayload = {
  title: string;
  type: string;
  type_id: number;
  type_code: string;
  type_image: string;
  user_name?: string;
};
export type CreateChatDirectConversationPayload = {
  message_content: string;
  message_type: string;
};
export type ConversationQueryResponse = { data: Conversation };

/**
 * Const
 */

export type ReactPlayerProgressTypes = {
  played: number;
  playedSeconds: number;
};

export type LiveStreamProductTypes = LiveStreamProduct;

export type PinnedProductTypes = LiveStreamProductTypes | null;

export type ProductRangeTypes = {
  range: number[];
  product: LiveStreamProductTypes;
};
export type fetchCommentPayload = {
  id: number;
  offset: number;
};
export type ListOfParticipantsPayload = {
  lottery_game_id: string;
  limit: number;
  offset: number;
  status?: string;
};
/**
 * Default !value
 */

export const FormChatDefault = { comment: '' };

export const PinnedProductDefault = null;
